import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren, ViewContainerRef } from '@angular/core';
import { LMBuilderModel } from "@components/builder/services/lm-builder-model.service";
import { LMFieldPickerService } from "@app/components";
import { LMErrorUtilService, LMUrlUtilService } from "@app/services";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { LMFieldPickerSelectOptions } from "@modal/field-picker/lm-field-picker";
import { SohoAlertDirective } from "ids-enterprise-ng";

@Component({
  selector:    'lm-builder-display-field-alerts',
  standalone: false,
  templateUrl: './lm-builder-display-field-alerts.component.html',
  styleUrl: './lm-builder-display-field-alerts.component.scss',
})

export class LMBuilderDisplayFieldAlertsComponent implements OnInit, OnDestroy {
  @Input() spec!: LMListPSpec;
  @Input() displayField!: LMDisplayFieldPSpec | undefined;
  @Input() fieldSpec: LMFieldPSpec | undefined;

  @ViewChildren(SohoAlertDirective) alerts: QueryList<SohoAlertDirective> | undefined;

  private destroyer$ = new Subject<void>();
  conditionValidator$:Subject<LMAlertValidationRequest> | undefined;
  redAlertMessage = '';
  yellowAlertMessage = '';
  greenAlertMessage ='';
  blueAlertMessage ='';
  iconAlertMessage ='';

  constructor(
    private lmBuilderModel: LMBuilderModel,
    private lmUrlUtilService: LMUrlUtilService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmFieldPickerService: LMFieldPickerService,
  ) {
      //no-op
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private initialize() {

    if (this.displayField) {
        this.redAlertMessage = this.initialAlertMessage(this.displayField.alerts.red);
        this.initialAlertCondition('red', this.displayField.alerts.red);
        this.yellowAlertMessage = this.initialAlertMessage(this.displayField.alerts.yellow);
        this.initialAlertCondition('yellow', this.displayField.alerts.yellow);
        this.greenAlertMessage = this.initialAlertMessage(this.displayField.alerts.green);
        this.initialAlertCondition('green', this.displayField.alerts.green);
        this.blueAlertMessage = this.initialAlertMessage(this.displayField.alerts.blue);
        this.initialAlertCondition('blue', this.displayField.alerts.blue);
        this.iconAlertMessage = this.initialAlertMessage(this.displayField.alerts.icon);
        this.initialAlertCondition('icon', this.displayField.alerts.icon);
    }

    if (!this.conditionValidator$) {
      this.conditionValidator$ = new Subject<LMAlertValidationRequest>();
      this.conditionValidator$.pipe(
          takeUntil(this.destroyer$),
          debounceTime(1250),
          distinctUntilChanged())
          .subscribe(request => {
            this.validateSimpleCondition(request.type, request.simpleCondition);
          });
    }

    // @ts-ignore
    Soho.Validation.rules['validateRequired'] = this.lmBuilderModel.validateRequired();
  }

  private initialAlertMessage(alertSpec: LMAlertPSpec | undefined): string {
    if (alertSpec && alertSpec.mouseOverTextIs) {

      return alertSpec.mouseOverTextIs.fields ? alertSpec.mouseOverTextIs.messageRule : alertSpec.mouseOverTextIs.display;
    }

    return '';
  }

  private initialAlertCondition(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon', alertSpec: LMAlertPSpec | undefined) {
    if (alertSpec && alertSpec.when.condition) {
      this.validateSimpleCondition(type, alertSpec.when.condition);
    }
  }


  updateNegated(negated: true | false, type?: 'red' | 'yellow' | 'green' | 'blue' | 'icon') {
    if (this.displayField) {
      switch (type) {
        case "red":
          this._updateNegated(this.displayField.alerts.red, negated);
          return;
        case "yellow":
          this._updateNegated(this.displayField.alerts.yellow, negated);
          return;
        case "green":
          this._updateNegated(this.displayField.alerts.green, negated);
          return;
        case "blue":
          this._updateNegated(this.displayField.alerts.blue, negated);
          return;
        case "icon":
          this._updateNegated(this.displayField.alerts.icon, negated);
          return;
      }
    }
  }

  private _updateNegated(alertSpec: LMAlertPSpec | undefined, negated: true | false) {
    if (alertSpec) {
      alertSpec.when.negated = negated;
    }
  }

  updateMessage(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon') {
    if (this.displayField) {
      switch (type) {
        case "red":
          this._updateMessage(this.displayField.alerts.red, this.redAlertMessage);
          return;
        case "yellow":
          this._updateMessage(this.displayField.alerts.yellow, this.yellowAlertMessage);
          return;
        case "green":
          this._updateMessage(this.displayField.alerts.green, this.greenAlertMessage);
          return;
        case "blue":
          this._updateMessage(this.displayField.alerts.blue, this.blueAlertMessage);
          return;
        case "icon":
          this._updateMessage(this.displayField.alerts.icon, this.iconAlertMessage);
          return;
      }
    }
  }

  private _updateMessage(alertSpec: LMAlertPSpec | undefined, message: string) {
    if (alertSpec) {
      alertSpec.mouseOverTextIs = { type: 'explicit', messageRule: message, display: message };
    }
  }

  addAlert(type: 'red' | 'yellow' | 'green' | 'blue') {
    if (this.displayField) {
      switch (type) {
        case "red":
          this.displayField.alerts.red = this.lmBuilderModel.buildAlert();
          return;
        case "yellow":
          this.displayField.alerts.yellow = this.lmBuilderModel.buildAlert();
          return;
        case "green":
          this.displayField.alerts.green = this.lmBuilderModel.buildAlert();
          return;
        case "blue":
          this.displayField.alerts.blue = this.lmBuilderModel.buildAlert();
          return;
      }
    }
  }

  removeAlert(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon') {
    if (this.displayField) {
      switch (type) {
        case "red":
          this.displayField.alerts.red = undefined;
          this.redAlertMessage = '';
          return;
        case "yellow":
          this.displayField.alerts.yellow = undefined;
          this.yellowAlertMessage = '';
          return;
        case "green":
          this.displayField.alerts.green = undefined;
          this.greenAlertMessage = '';
          return;
        case "blue":
          this.displayField.alerts.blue = undefined;
          this.blueAlertMessage = '';
          return;
        case "icon":
          this.displayField.alerts.icon = undefined;
          this.iconAlertMessage = '';
          return;
      }
    }
  }

  lookupRedClick = () => {
   this.lookupClick('red');
  };

  lookupYellowClick = () => {
    this.lookupClick('yellow');
  };

  lookupGreenClick = () => {
    this.lookupClick('green');
  };

  lookupBlueClick = () => {
    this.lookupClick('blue');
  };

  lookupIconClick = () => {
    this.lookupClick('icon');
  };

  private lookupClick(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon') {

    let fieldPickerUrl = this.lmBuilderModel.getBuilderUrl();
    fieldPickerUrl = this.lmUrlUtilService.addParamToUrl(fieldPickerUrl, 'requesttype', 'fieldSpecForBusClass');

    const options: LMFieldPickerSelectOptions = {
      view: 'SimpleConditions',
      businessClass: this.lmBuilderModel.listResource.busClassName,
      mode: 'basic'
    };

    this.lmFieldPickerService.openFieldPickerDialog(options, fieldPickerUrl, undefined as unknown as ViewContainerRef)
        .subscribe((result) => {
          if (result && this.displayField) {
            switch (type) {
              case "red":
                this.updateAlertCondition(this.displayField.alerts.red, result.fieldName);
                return;
              case "yellow":
                this.updateAlertCondition(this.displayField.alerts.yellow, result.fieldName);
                return;
              case "green":
                this.updateAlertCondition(this.displayField.alerts.green, result.fieldName);
                return;
              case "blue":
                this.updateAlertCondition(this.displayField.alerts.blue, result.fieldName);
                return;
              case "icon":
                this.updateAlertCondition(this.displayField.alerts.icon, result.fieldName);
                return;
            }
          }
        });
  }

  private updateAlertCondition(alertSpec: LMAlertPSpec | undefined, simpleCondition: string) {
    if (alertSpec) {
      alertSpec.when.condition =simpleCondition;
    }
  }
  private validateSimpleCondition(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon', simpleCondition: string) {
    this.lmBuilderModel.validateSimpleCondition(simpleCondition).pipe(
        takeUntil(this.destroyer$),
        debounceTime(1250),
        distinctUntilChanged()).subscribe({
      next: (v)       => { this.handleValidationResponse(type, v); },
      error: (e)      => { this.handleError(e); },
      complete: ()    => { }
    });
  }

  private handleValidationResponse(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon', response: LMLplValidationResponse) {
    this.updateValidationAlert(type, response);
  }

  private updateValidationAlert(type: 'red' | 'yellow' | 'green' | 'blue' | 'icon', lplValidationResponse?: LMLplValidationResponse) {
    let alertId = '';

    switch (type) {
      case "red":
        alertId = 'lm-display-field-red-alert-when';
        break;
      case "yellow":
        alertId = 'lm-display-field-yellow-alert-when';
        break;
      case "green":
        alertId = 'lm-display-field-green-alert-when';
        break;
      case "blue":
        alertId = 'lm-display-field-blue-alert-when';
        break;
      case "icon":
        alertId = 'lm-display-field-icon-alert-when';
        break;
    }

    const alert = this.alerts?.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === alertId);
    if (alert) {
      if (!lplValidationResponse || lplValidationResponse.lplStatus === 'LPL_VALID') {
        alert.removeAllMessages();
      } else {
        const validationError = lplValidationResponse.validationError;
        if (validationError) {
          let alertMessage = validationError.errorDescription;
          if (validationError.errorText) {
            alertMessage += ' ' + validationError.errorText;
          }
          const alertType = validationError && validationError.isWarning ? 'info' : 'error';
          alert.addInlineMessage(alertMessage, alertType, true, false, undefined, alertId);
        } else {
          alert.addInlineMessage(lplValidationResponse.message ? lplValidationResponse.message : 'Unexpected Error', 'error', true, false, undefined, alertId);
        }
      }
    }
  }

  private handleError(error: any) {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }
}
